/**
 * Which Hairo entry point is on screen.
 *
 * The sidebar "Ask Hairo" card reports here while it is laid out (the rail is
 * showing). The floating blob (CopilotLauncher) only renders when no card is
 * visible: full-width pages without the sidebar, and tablets and phones where
 * the rail is hidden behind the menu drawer.
 */
import { useEffect, useSyncExternalStore } from "react";

const visible = new Set();
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn());

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const getSnapshot = () => visible.size > 0;

/** Marks one card as visible (true) or not (false). */
export function setCardVisible(token, isVisible) {
  const had = visible.has(token);
  if (isVisible && !had) visible.add(token);
  else if (!isVisible && had) visible.delete(token);
  else return;
  emit();
}

/** True while any sidebar Hairo card is laid out on screen. */
export function useSidebarCardVisible() {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/**
 * Reports `ref`'s element as a visible card while it has a non-zero width.
 * A rail inside display:none (hidden on tablets and phones) measures 0.
 */
export function useReportCardVisible(ref, enabled = true) {
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;
    const token = {};
    const update = () => {
      const rect = el.getBoundingClientRect?.();
      setCardVisible(token, Boolean(rect && rect.width > 0));
    };
    update();
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(update);
      observer.observe(el);
    }
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      setCardVisible(token, false);
    };
  }, [ref, enabled]);
}

/** Test helper: forget every card. */
export function resetLauncherSlot() {
  visible.clear();
  emit();
}
